<template>
    <!-- Модальное окно для работы с товарами в TG боте на полный экран -->
    <div class="tg-product-modal-fullscreen">
        <!-- Шапка с заголовком -->
        <div class="modify-form-header">
            <div class="header-icon">{{ isAdd ? '➕' : '✏️' }}</div>
            <h1>{{ isAdd ? 'Добавить товар' : 'Редактировать товар' }}</h1>
            <p class="header-subtitle">{{ isAdd ? 'Заполните информацию о новом товаре' : 'Измените данные товара' }}</p>
        </div>

        <!-- Основная форма с прокруткой -->
        <div class="form-scroll-container">
            <form @submit.prevent="submitForm" class="tg-product-form-fullscreen" enctype="multipart/form-data">
                <!-- Информация о товаре -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">📦</span>
                        Информация о товаре
                    </h2>
                    
                    <div class="form-grid-fullscreen">
                        <!-- Выбор товара (только при добавлении) -->
                        <div class="form-group-fullscreen" v-if="isAdd">
                            <label for="productSelect" class="form-label-fullscreen">
                                <span class="label-icon-fullscreen">🎯</span>
                                Выберите товар *
                            </label>
                            <div class="custom-select-wrapper">
                                <select 
                                    id="productSelect" 
                                    v-model="formData.product_id" 
                                    class="custom-select"
                                    required
                                    @change="onProductSelect"
                                >
                                    <option value="" disabled class="placeholder-option">Выберите товар из базы</option>
                                    <option 
                                        v-for="product in availableProducts" 
                                        :key="product.id" 
                                        :value="product.id"
                                        :disabled="isProductInBot(product.id)"
                                        class="select-option"
                                    >
                                        <span class="option-text">{{ product.name }}</span>
                                        <span v-if="isProductInBot(product.id)" class="option-badge">уже в боте</span>
                                    </option>
                                </select>
                                <div class="select-arrow">
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                            </div>
                            <div v-if="formData.product_id" class="selected-product-info">
                                <div class="info-icon">✓</div>
                                <span class="info-text">Выбран: {{ getSelectedProductName() }}</span>
                            </div>
                        </div>

                        <!-- Название товара в боте -->
                        <div class="form-group-fullscreen" v-if="formData.product_name">
                            <label class="form-label-fullscreen">
                                <span class="label-icon-fullscreen">🏷️</span>
                                Название в боте
                            </label>
                            <div class="product-name-display-fullscreen">
                                <div class="name-icon">📋</div>
                                <span class="product-name-text">{{ formData.product_name }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Цены и DROP режим -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">💰</span>
                        Цены и DROP режим
                    </h2>
                    
                    <div class="form-grid-fullscreen">
                        <!-- DROP режим -->
                        <div class="form-group-fullscreen">
                            <label class="form-label-fullscreen">
                                <span class="label-icon-fullscreen">🔥</span>
                                DROP режим
                            </label>
                            <div class="drop-mode-toggle">
                                <button 
                                    type="button"
                                    @click="toggleDropMode"
                                    :class="{ 'active': formData.is_drop }"
                                    class="drop-mode-btn"
                                >
                                    <div class="drop-mode-content">
                                        <div class="drop-mode-icon-wrapper">
                                            <span class="drop-mode-icon">{{ formData.is_drop ? '⚡' : '📊' }}</span>
                                        </div>
                                        <div class="drop-mode-text-wrapper">
                                            <span class="drop-mode-text">
                                                {{ formData.is_drop ? 'DROP активирован' : 'Обычный режим' }}
                                            </span>
                                            <span class="drop-mode-subtext">
                                                {{ formData.is_drop ? 'Специальная цена активирована' : 'Стандартная цена' }}
                                            </span>
                                        </div>
                                        <div class="drop-mode-badge" v-if="formData.is_drop">
                                            <span class="badge-icon">🔥</span>
                                            <span class="badge-text">Скидка {{ calculateDiscountPercentage() }}%</span>
                                        </div>
                                    </div>
                                </button>
                            </div>
                            <p class="form-hint-fullscreen">
                                В DROP режиме товар продается по специальной цене
                            </p>
                        </div>

                        <!-- Ценовые поля -->
                        <div class="price-fields-container">
                            <!-- Розничная цена -->
                            <div class="form-group-fullscreen price-field">
                                <label for="retailPrice" class="form-label-fullscreen">
                                    <span class="label-icon-fullscreen">🏪</span>
                                    Розничная цена *
                                </label>
                                <div class="price-input-container">
                                    <div class="price-input-wrapper">
                                        <input 
                                            type="number" 
                                            id="retailPrice" 
                                            v-model.number="formData.retail_price" 
                                            step="0.01"
                                            min="0.01"
                                            placeholder="0.00"
                                            required
                                            class="price-input"
                                            @input="onRetailPriceChange"
                                        />
                                        <div class="currency-display">₽</div>
                                    </div>
                                    <div class="price-info">
                                        <div class="info-icon">💼</div>
                                        <span class="info-text">Стандартная цена продажи</span>
                                    </div>
                                </div>
                            </div>

                            <!-- DROP цена (видна только при активном DROP) -->
                            <div class="form-group-fullscreen price-field" v-if="formData.is_drop">
                                <label for="dropPrice" class="form-label-fullscreen">
                                    <span class="label-icon-fullscreen">⚡</span>
                                    DROP цена *
                                </label>
                                <div class="price-input-container">
                                    <div class="price-input-wrapper drop-price">
                                        <input 
                                            type="number" 
                                            id="dropPrice" 
                                            v-model.number="formData.drop_price" 
                                            step="0.01"
                                            min="0.01"
                                            placeholder="0.00"
                                            :required="formData.is_drop"
                                            class="price-input"
                                            @input="onDropPriceChange"
                                        />
                                        <div class="currency-display">₽</div>
                                    </div>
                                    <div class="price-info" v-if="formData.drop_price && formData.retail_price && formData.drop_price < formData.retail_price">
                                        <div class="info-icon">🎯</div>
                                        <span class="info-text">
                                            Скидка: <span class="discount-text">{{ calculateDiscountPercentage() }}%</span> 
                                            (экономия {{ calculateDiscountAmount() }}₽)
                                        </span>
                                    </div>
                                    <div class="price-info" v-else-if="formData.is_drop">
                                        <div class="info-icon">💡</div>
                                        <span class="info-text">Установите специальную DROP цену</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Таблица размеров -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">📊</span>
                        Таблица размеров
                    </h2>

                    <div class="size-table-recognition-section">
                        <p class="form-hint-fullscreen">
                            Можно загрузить фото размерной таблицы (например, рукописной) — она будет распознана и подставлена ниже автоматически.
                        </p>
                        <div class="file-upload-fullscreen">
                            <input
                                type="file"
                                id="sizeTablePhotoUpload"
                                accept="image/*"
                                @change="handleSizeTablePhotoUpload"
                                :disabled="isRecognizingSizeTable"
                                class="file-input-fullscreen"
                            />
                            <label for="sizeTablePhotoUpload" class="file-upload-label-fullscreen" :class="{ 'disabled': isRecognizingSizeTable }">
                                <div class="upload-icon-container">
                                    <div class="upload-icon-circle">
                                        <span class="upload-icon-fullscreen">{{ isRecognizingSizeTable ? '⏳' : '🖼️' }}</span>
                                    </div>
                                    <span class="upload-text-fullscreen">
                                        {{ isRecognizingSizeTable ? 'Распознаём таблицу...' : 'Распознать таблицу по фото' }}
                                    </span>
                                </div>
                                <div class="upload-details">
                                    <span class="upload-hint-fullscreen">(до 8MB)</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div class="size-table-json-section">
                        <div class="size-table-header">
                            <p class="form-hint-fullscreen">
                                Создайте таблицу размеров. Каждая строка - размер, каждая колонка - характеристика.
                            </p>
                            
                            <div class="size-table-controls">
                                <button 
                                    type="button" 
                                    @click="addNewSizeRow"
                                    class="control-btn add-btn"
                                >
                                    <span class="control-icon">➕</span>
                                    Добавить размер
                                </button>
                                <button 
                                    type="button" 
                                    @click="addNewCharacteristic"
                                    class="control-btn add-char-btn"
                                >
                                    <span class="control-icon">📐</span>
                                    Добавить характеристику
                                </button>
                                <button 
                                    type="button" 
                                    @click="removeLastSizeRow"
                                    class="control-btn remove-btn"
                                    :disabled="sizeTableData.length === 0"
                                >
                                    <span class="control-icon">➖</span>
                                    Удалить последний размер
                                </button>
                            </div>
                        </div>
                        
                        <!-- Простая таблица с адаптивными ширинами колонок -->
                        <div class="size-table-editor" v-if="sizeTableData.length > 0">
                            <div class="table-container">
                                <table class="simple-size-table">
                                    <thead>
                                        <tr>
                                            <!-- Колонка "Размер" - фиксированная ширина -->
                                            <th class="size-header">
                                                <div class="header-content">
                                                    <span>Размер</span>
                                                </div>
                                            </th>
                                            
                                            <!-- Динамические колонки характеристик - растягиваются по содержимому -->
                                            <th 
                                                v-for="char in characteristics" 
                                                :key="char"
                                                class="char-header"
                                                :style="{ minWidth: getColumnWidth(char) }"
                                            >
                                                <div class="header-content">
                                                    <span class="char-name">{{ char }}</span>
                                                    <button 
                                                        type="button" 
                                                        @click="removeCharacteristic(char)"
                                                        class="remove-char-btn"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </th>
                                            
                                            <!-- Колонка "Действия" - фиксированная ширина -->
                                            <th class="actions-header">
                                                <div class="header-content">Действия</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <!-- Динамические строки -->
                                        <tr 
                                            v-for="(sizeRow, index) in sizeTableData" 
                                            :key="index"
                                            class="size-row"
                                        >
                                            <!-- Ячейка размера -->
                                            <td class="size-cell">
                                                <input 
                                                    type="text" 
                                                    v-model="sizeRow.size"
                                                    placeholder="Например: S"
                                                    class="size-input"
                                                    required
                                                />
                                            </td>
                                            
                                            <!-- Ячейки характеристик -->
                                            <td 
                                                v-for="char in characteristics" 
                                                :key="char"
                                                class="char-cell"
                                                :style="{ minWidth: getColumnWidth(char) }"
                                            >
                                                <input 
                                                    type="text" 
                                                    v-model="sizeRow.values[char]"
                                                    placeholder="Введите значение"
                                                    class="char-input"
                                                />
                                            </td>
                                            
                                            <!-- Ячейка действий -->
                                            <td class="actions-cell">
                                                <button 
                                                    type="button" 
                                                    @click="removeSizeRow(index)"
                                                    class="remove-row-btn"
                                                >
                                                    ✕
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            <!-- Предпросмотр JSON -->
                            <div class="json-preview">
                                <h3 class="preview-title">Предпросмотр JSON:</h3>
                                <pre class="json-output">{{ sizeTableJson }}</pre>
                            </div>
                        </div>
                        
                        <!-- Сообщение если нет размеров -->
                        <div v-else class="no-sizes-message">
                            <div class="message-icon">📋</div>
                            <p class="message-text">Нажмите "Добавить размер", чтобы начать создание таблицы размеров</p>
                        </div>
                    </div>
                </div>

                <!-- Фото товара -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">📷</span>
                        Фото для бота
                    </h2>
                    
                    <div class="photo-upload-section-fullscreen">
                        <div v-if="displayedPhoto" class="current-photo-container">
                            <div class="photo-preview-fullscreen">
                                <img :src="displayedPhoto" alt="Preview" class="preview-img-fullscreen" />
                                <button 
                                    type="button" 
                                    @click="removePhoto"
                                    class="remove-photo-btn-fullscreen"
                                >
                                    ✕
                                </button>
                            </div>
                            <div class="current-photo-info">
                                <div class="info-icon">📸</div>
                                <span>Текущее фото товара</span>
                            </div>
                        </div>
                        
                        <div class="file-upload-fullscreen">
                            <input 
                                type="file" 
                                id="photoUpload"
                                accept="image/*"
                                @change="handlePhotoUpload"
                                class="file-input-fullscreen"
                            />
                            <label for="photoUpload" class="file-upload-label-fullscreen">
                                <div class="upload-icon-container">
                                    <div class="upload-icon-circle">
                                        <span class="upload-icon-fullscreen">📁</span>
                                    </div>
                                    <span class="upload-text-fullscreen">
                                        {{ displayedPhoto ? 'Изменить фото' : 'Загрузить фото' }}
                                    </span>
                                </div>
                                <div class="upload-details">
                                    <span class="upload-hint-fullscreen">(до 5MB, квадратное изображение)</span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Ссылка на пак фотографий (НОВОЕ ПОЛЕ) -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">🖼️</span>
                        Пак фотографий
                    </h2>
                    
                    <div class="photo-pack-section">
                        <div class="form-group-fullscreen">
                            <label for="photoPackUrl" class="form-label-fullscreen">
                                <span class="label-icon-fullscreen">🔗</span>
                                Ссылка на пак фотографий
                            </label>
                            <div class="url-input-container">
                                <div class="url-input-wrapper">
                                    <div class="url-icon">🌐</div>
                                    <input 
                                        type="url" 
                                        id="photoPackUrl" 
                                        v-model="formData.photo_pack_url" 
                                        placeholder="https://example.com/photos или @username"
                                        class="url-input"
                                    />
                                </div>
                                <div class="url-info">
                                    <div class="info-icon">💡</div>
                                    <span class="info-text">
                                        Ссылка на Яндекс.Диск, Google Drive, Telegram канал или другой источник с фотографиями товара
                                    </span>
                                </div>
                            </div>
                            <p class="form-hint-fullscreen">
                                Добавьте ссылку на полный пак фотографий для детального просмотра товара
                            </p>
                        </div>

                        <!-- Отображение текущей ссылки (если есть) -->
                        <div v-if="formData.photo_pack_url" class="current-photo-pack">
                            <div class="pack-info">
                                <div class="pack-icon">📎</div>
                                <span class="pack-label">Текущая ссылка:</span>
                                <a :href="formData.photo_pack_url" target="_blank" class="pack-link">
                                    {{ formData.photo_pack_url }}
                                </a>
                                <button 
                                    type="button" 
                                    @click="formData.photo_pack_url = ''"
                                    class="remove-pack-btn"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Этикетки -->
                <div class="form-section-fullscreen">
                    <h2 class="section-title-fullscreen">
                        <span class="section-icon-fullscreen">🏷️</span>
                        Этикетки (теги)
                    </h2>
                    
                    <div class="labels-section-fullscreen">
                        <div v-if="availableLabels.length > 0" class="available-labels-fullscreen">
                            <p class="form-hint-fullscreen">Выберите этикетки для товара:</p>
                            <div class="labels-grid-fullscreen">
                                <div 
                                    v-for="label in availableLabels" 
                                    :key="label.id"
                                    class="label-item-fullscreen"
                                    :class="{ 'selected': isLabelSelected(label.id) }"
                                    @click="toggleLabel(label.id)"
                                >
                                    <div class="label-content">
                                        <span 
                                            class="label-color-dot-fullscreen"
                                            :style="{ backgroundColor: label.color }"
                                        ></span>
                                        <span class="label-name-fullscreen">{{ label.label_name }}</span>
                                        <span class="label-check-icon" v-if="isLabelSelected(label.id)">
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div v-if="selectedLabels.length > 0" class="selected-labels-fullscreen">
                            <p class="form-hint-fullscreen">Выбранные этикетки:</p>
                            <div class="selected-labels-list-fullscreen">
                                <div 
                                    v-for="labelId in selectedLabels" 
                                    :key="labelId"
                                    class="selected-label-item-fullscreen"
                                >
                                    <div class="selected-label-content">
                                        <span 
                                            class="label-color-dot-fullscreen"
                                            :style="{ backgroundColor: getLabelColor(labelId) }"
                                        ></span>
                                        <span class="label-name-fullscreen">{{ getLabelName(labelId) }}</span>
                                        <button 
                                            type="button" 
                                            @click="removeLabel(labelId)"
                                            class="remove-label-btn-fullscreen"
                                        >
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="availableLabels.length === 0" class="no-labels-message">
                            <div class="message-icon">🏷️</div>
                            <p class="message-text">Нет доступных этикеток. Создайте этикетки в разделе управления продуктами.</p>
                        </div>
                    </div>
                </div>

                <!-- Кнопки действий -->
                <div class="form-actions-fullscreen">
                    <button 
                        type="submit" 
                        class="btn-fullscreen btn-submit-fullscreen"
                        :disabled="isSubmitting || !isFormValid"
                    >
                        <span v-if="isSubmitting" class="submit-spinner-fullscreen"></span>
                        <span v-else class="btn-content">
                            <span class="btn-icon">{{ isAdd ? '➕' : '💾' }}</span>
                            <span class="btn-text">{{ isAdd ? 'Добавить в бот' : 'Сохранить изменения' }}</span>
                        </span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import api from '@/api/api';

export default {
    name: 'TgProductModalFullscreen',
    props: {
        isAdd: {
            type: Boolean,
            default: true,
            required: true
        },
        productData: {
            type: Object,
            default: () => ({})
        },
        availableProducts: {
            type: Array,
            default: () => []
        },
        availableLabels: {
            type: Array,
            default: () => []
        },
        tgBotProducts: {
            type: Array,
            default: () => []
        }
    },
    emits: ['submit'],
    data() {
        return {
            formData: {
                product_id: null,
                product_name: '',
                is_drop: false,
                drop_price: 0,
                retail_price: 0,
                photo_url: '',
                photo_pack_url: '', // НОВОЕ ПОЛЕ: ссылка на пак фотографий
                size_table: [],
                label_ids: [] // Храним только ID этикеток
            },
            
            sizeTableData: [],
            characteristics: ['Длина', 'Талия', 'Выход (застегнуто)', 'Выход (растегнуто)'],
            isRecognizingSizeTable: false,
            
            photo: null,
            selectedFile: null,
            
            isSubmitting: false,
            isFormFilled: false,
            
            minColumnWidth: 100,
            maxColumnWidth: 200
        };
    },
    computed: {
        isFormValid() {
            if (this.isAdd && !this.formData.product_id) return false;
            if (!this.formData.retail_price || this.formData.retail_price <= 0) return false;
            if (this.formData.is_drop && (!this.formData.drop_price || this.formData.drop_price <= 0)) return false;
            return this.sizeTableData.length > 0;
        },
        
        // ИСПРАВЛЕНИЕ: Убеждаемся, что возвращаем массив чисел (ID)
        selectedLabels() {
            const ids = this.formData.label_ids || [];
            
            // Преобразуем все элементы в числа
            const numericIds = ids
                .map(item => {
                    // Если это объект, берем его id, иначе само значение
                    if (typeof item === 'object' && item !== null) {
                        return item.id || item;
                    }
                    return item;
                })
                .map(id => Number(id))
                .filter(id => !isNaN(id) && id > 0);
            
            console.log('🔍 computed selectedLabels:', numericIds);
            return numericIds;
        },
        
        displayedPhoto() {
            return this.photo || (this.productData && this.productData.photo_url) || null;
        },
        
        sizeTableJson() {
            return JSON.stringify(this.sizeTableData, null, 2);
        }
    },
    created() {
        this.initializeForm();
    },
    mounted() {
        this.setupTelegramBackButton();
    },
    beforeUnmount() {
        this.cleanupTelegramBackButton();
    },
    watch: {
        productData: {
            handler(newData) {
                if (!this.isAdd && newData && Object.keys(newData).length > 0 && !this.isFormFilled) {
                    this.fillFormWithProductData(newData);
                }
            },
            deep: true,
            immediate: true
        },
        
        // Следим за изменением retail_price и обновляем drop_price
        'formData.retail_price': function(newPrice) {
            if (!this.formData.is_drop) {
                // Если DROP режим выключен, drop_price всегда равен retail_price
                this.formData.drop_price = newPrice;
            }
        },
        
        // Следим за изменением is_drop
        'formData.is_drop': function(newValue) {
            if (!newValue) {
                // Если выключили DROP режим, принудительно устанавливаем drop_price = retail_price
                this.formData.drop_price = this.formData.retail_price;
            } else {
                // Если включили DROP режим, устанавливаем drop_price в retail_price
                this.formData.drop_price = this.formData.retail_price;
            }
        }
    },
    methods: {
        getColumnWidth(charName) {
            const baseWidth = charName.length * 8 + 40;
            let width = Math.max(this.minColumnWidth, Math.min(this.maxColumnWidth, baseWidth));
            return `${width}px`;
        },
        
        setupTelegramBackButton() {
            if (window.Telegram?.WebApp?.BackButton) {
                window.Telegram.WebApp.BackButton.onClick(this.handleBackButton);
                window.Telegram.WebApp.BackButton.show();
            }
        },
        
        cleanupTelegramBackButton() {
            if (window.Telegram?.WebApp?.BackButton) {
                window.Telegram.WebApp.BackButton.offClick(this.handleBackButton);
                window.Telegram.WebApp.BackButton.hide();
            }
        },
        
        handleBackButton() {
            this.$emit('close');
        },
        
        toggleDropMode() {
            const newIsDrop = !this.formData.is_drop;
            this.formData.is_drop = newIsDrop;
            
            if (newIsDrop) {
                // При включении DROP режима устанавливаем drop_price в retail_price
                this.formData.drop_price = this.formData.retail_price || 0;
            } else {
                // При выключении DROP режима принудительно устанавливаем drop_price = retail_price
                this.formData.drop_price = this.formData.retail_price;
            }
        },
        
        calculateDiscountPercentage() {
            if (!this.formData.is_drop || !this.formData.retail_price || !this.formData.drop_price) {
                return 0;
            }
            
            // Если drop_price равен retail_price, скидки нет
            if (this.formData.drop_price >= this.formData.retail_price) {
                return 0;
            }
            
            const discount = ((this.formData.retail_price - this.formData.drop_price) / this.formData.retail_price) * 100;
            return Math.round(discount);
        },
        
        calculateDiscountAmount() {
            if (!this.formData.is_drop || !this.formData.retail_price || !this.formData.drop_price) {
                return 0;
            }
            
            // Если drop_price равен retail_price, скидки нет
            if (this.formData.drop_price >= this.formData.retail_price) {
                return 0;
            }
            
            return Math.round((this.formData.retail_price - this.formData.drop_price) * 100) / 100;
        },
        
        onDropPriceChange() {
            // Если DROP режим включен, проверяем, чтобы drop_price не превышал retail_price
            if (this.formData.is_drop) {
                if (this.formData.drop_price > this.formData.retail_price) {
                    this.formData.drop_price = this.formData.retail_price;
                }
            } else {
                // Если DROP режим выключен, принудительно устанавливаем drop_price = retail_price
                this.formData.drop_price = this.formData.retail_price;
            }
        },
        
        onRetailPriceChange() {
            // Всегда обновляем drop_price при изменении retail_price
            this.formData.drop_price = this.formData.retail_price;
            
            // Если DROP режим включен, дополнительно проверяем, чтобы drop_price не превышал retail_price
            if (this.formData.is_drop && this.formData.drop_price > this.formData.retail_price) {
                this.formData.drop_price = this.formData.retail_price;
            }
        },
        
        initializeForm() {
            if (this.isAdd) {
                this.resetForm();
                this.addNewSizeRow();
            } else {
                if (this.productData && Object.keys(this.productData).length > 0) {
                    this.fillFormWithProductData(this.productData);
                }
            }
        },
        
        // ИСПРАВЛЕНИЕ: Извлекаем только ID из объектов этикеток
        fillFormWithProductData(productData) {
            console.log('🔄 Заполняем форму данными товара из бота:', productData);
            
            // Извлекаем ID этикеток из объектов
            let labelIds = [];
            if (productData.label_ids && Array.isArray(productData.label_ids)) {
                // Если пришли уже ID
                labelIds = productData.label_ids.map(id => Number(id)).filter(id => !isNaN(id) && id > 0);
            } else if (productData.labels && Array.isArray(productData.labels)) {
                // Если пришли объекты этикеток
                labelIds = productData.labels
                    .map(label => label.id || label)
                    .map(id => Number(id))
                    .filter(id => !isNaN(id) && id > 0);
            }
            
            console.log('🔍 Извлеченные ID этикеток:', labelIds);
            
            // Получаем значения цен
            const retailPrice = productData.retail_price || 0;
            const dropPrice = productData.drop_price || retailPrice; // Если drop_price нет, используем retail_price
            const isDrop = productData._is_drop_ || productData.is_drop || false;
            
            this.formData = {
                product_id: productData.product_id || productData.product?.id || null,
                product_name: productData.product_name || '',
                is_drop: isDrop,
                drop_price: dropPrice,
                retail_price: retailPrice,
                photo_url: productData.photo_url || '',
                photo_pack_url: productData.photo_pack || '', // НОВОЕ ПОЛЕ: загружаем ссылку
                size_table: productData.size_table || [],
                label_ids: labelIds // Сохраняем только ID
            };
            
            // Важно: если DROP режим выключен, гарантируем, что drop_price = retail_price
            if (!isDrop) {
                this.formData.drop_price = retailPrice;
            }
            
            if (productData.photo_url) {
                this.photo = productData.photo_url;
            }
            
            if (productData.size_table && Array.isArray(productData.size_table) && productData.size_table.length > 0) {
                this.loadSizeTableData(productData.size_table);
            } else {
                this.addNewSizeRow();
            }
            
            this.isFormFilled = true;
            console.log('✅ Форма заполнена:', this.formData);
        },
        
        loadSizeTableData(sizeTable) {
            this.sizeTableData = sizeTable.map(item => ({
                size: item.size || '',
                values: { ...item.values }
            }));
            
            if (sizeTable.length > 0 && sizeTable[0].values) {
                this.characteristics = Object.keys(sizeTable[0].values);
            }
        },
        
        isProductInBot(productId) {
            return this.tgBotProducts.some(product => product.product_id === productId);
        },
        
        getSelectedProductName() {
            if (!this.formData.product_id || !this.availableProducts) return '';
            const product = this.availableProducts.find(p => p.id === this.formData.product_id);
            return product ? product.name : '';
        },
        
        onProductSelect() {
            const product = this.availableProducts.find(p => p.id === this.formData.product_id);
            if (product) {
                this.formData.product_name = product.name;
                
                if (product.purchase_price) {
                    this.formData.retail_price = Math.round(product.purchase_price * 1.5 * 100) / 100;
                    
                    // Всегда устанавливаем drop_price равным retail_price при выборе товара
                    this.formData.drop_price = this.formData.retail_price;
                }
            }
        },
        
        addNewSizeRow() {
            const newRow = {
                size: '',
                values: {}
            };
            
            this.characteristics.forEach(char => {
                newRow.values[char] = '';
            });
            
            this.sizeTableData.push(newRow);
        },
        
        removeSizeRow(index) {
            if (this.sizeTableData.length > 1) {
                this.sizeTableData.splice(index, 1);
            } else {
                this.showError('Должен остаться хотя бы один размер');
            }
        },
        
        removeLastSizeRow() {
            if (this.sizeTableData.length > 0) {
                this.sizeTableData.pop();
            }
        },
        
        addNewCharacteristic() {
            const charName = prompt('Введите название новой характеристики:');
            if (charName && charName.trim()) {
                const trimmedName = charName.trim();
                if (!this.characteristics.includes(trimmedName)) {
                    this.characteristics.push(trimmedName);
                    
                    this.sizeTableData.forEach(row => {
                        row.values[trimmedName] = '';
                    });
                }
            }
        },
        
        removeCharacteristic(charName) {
            if (this.characteristics.length > 1) {
                if (confirm(`Удалить характеристику "${charName}"?`)) {
                    const index = this.characteristics.indexOf(charName);
                    if (index !== -1) {
                        this.characteristics.splice(index, 1);
                        
                        this.sizeTableData.forEach(row => {
                            delete row.values[charName];
                        });
                    }
                }
            } else {
                this.showError('Должна остаться хотя бы одна характеристика');
            }
        },
        
        // ИСПРАВЛЕНИЕ: Работаем с числовыми ID
        isLabelSelected(labelId) {
            const numericId = Number(labelId);
            return this.selectedLabels.includes(numericId);
        },
        
        // ИСПРАВЛЕНИЕ: Добавляем/удаляем числовые ID
        toggleLabel(labelId) {
            const numericId = Number(labelId);
            
            // Создаем копию массива
            const currentIds = [...(this.formData.label_ids || [])];
            
            // Преобразуем существующие элементы в числа
            const normalizedIds = currentIds.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return item.id || item;
                }
                return Number(item);
            }).filter(id => !isNaN(id));
            
            const index = normalizedIds.indexOf(numericId);
            
            if (index === -1) {
                // Добавляем новый ID
                normalizedIds.push(numericId);
            } else {
                // Удаляем существующий ID
                normalizedIds.splice(index, 1);
            }
            
            // Сохраняем обратно в formData
            this.formData.label_ids = normalizedIds;
            
            console.log('🔍 После toggleLabel:', normalizedIds);
        },
        
        // ИСПРАВЛЕНИЕ: Удаляем по числовому ID
        removeLabel(labelId) {
            const numericId = Number(labelId);
            const currentIds = this.formData.label_ids || [];
            
            // Ищем и удаляем ID
            const updatedIds = currentIds
                .map(item => {
                    if (typeof item === 'object' && item !== null) {
                        return item.id || item;
                    }
                    return item;
                })
                .map(id => Number(id))
                .filter(id => !isNaN(id) && id !== numericId);
            
            this.formData.label_ids = updatedIds;
            console.log('🔍 После removeLabel:', updatedIds);
        },
        
        getLabelName(labelId) {
            const label = this.availableLabels.find(l => l.id === labelId);
            return label ? label.label_name : `Этикетка #${labelId}`;
        },
        
        getLabelColor(labelId) {
            const label = this.availableLabels.find(l => l.id === labelId);
            return label ? label.color : '#25A5F0';
        },
        
        handlePhotoUpload(event) {
            const file = event.target.files[0];
            
            if (!file) return;
            
            if (file.size > 5 * 1024 * 1024) {
                this.showError('Файл слишком большой. Максимальный размер: 5MB');
                event.target.value = '';
                return;
            }
            
            if (!file.type.startsWith('image/')) {
                this.showError('Пожалуйста, выберите изображение');
                event.target.value = '';
                return;
            }
            
            this.selectedFile = file;
            
            const reader = new FileReader();
            reader.onload = (e) => {
                this.photo = e.target.result;
            };
            reader.onerror = () => {
                this.showError('Ошибка чтения файла');
                this.selectedFile = null;
                this.photo = null;
            };
            reader.readAsDataURL(file);
            
            event.target.value = '';
        },
        
        removePhoto() {
            this.photo = null;
            this.selectedFile = null;
            this.formData.photo_url = '';
        },

        async handleSizeTablePhotoUpload(event) {
            const file = event.target.files[0];

            if (!file) return;

            if (file.size > 8 * 1024 * 1024) {
                this.showError('Файл слишком большой. Максимальный размер: 8MB');
                event.target.value = '';
                return;
            }

            if (!file.type.startsWith('image/')) {
                this.showError('Пожалуйста, выберите изображение');
                event.target.value = '';
                return;
            }

            this.isRecognizingSizeTable = true;

            try {
                const photoFormData = new FormData();
                photoFormData.append('photo', file);

                const response = await api.post('/bot_managment/size-table/recognize', photoFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                this.loadSizeTableData(response.data.size_table);
            } catch (error) {
                this.showError(error.response?.data?.detail || 'Не удалось распознать таблицу на фото');
            } finally {
                this.isRecognizingSizeTable = false;
                event.target.value = '';
            }
        },

        async submitForm() {
            try {
                if (this.isSubmitting) return;

                // Валидация формы
                if (this.isAdd && !this.formData.product_id) {
                    this.showError('Выберите товар из базы');
                    return;
                }

                if (!this.formData.retail_price || this.formData.retail_price <= 0) {
                    this.showError('Укажите корректную розничную цену');
                    return;
                }

                // ГАРАНТИЯ: Если DROP режим выключен, устанавливаем drop_price = retail_price
                if (!this.formData.is_drop) {
                    this.formData.drop_price = this.formData.retail_price;
                } else if (this.formData.is_drop && (!this.formData.drop_price || this.formData.drop_price <= 0)) {
                    this.showError('Укажите DROP цену');
                    return;
                }

                if (this.sizeTableData.length === 0) {
                    this.showError('Добавьте хотя бы один размер в таблицу');
                    return;
                }

                // Проверяем заполнение размеров
                const invalidSize = this.sizeTableData.find(row => !row.size || !row.size.trim());
                if (invalidSize) {
                    this.showError('Заполните название для всех размеров');
                    return;
                }

                // Валидация URL для photo_pack_url (если указан)
                if (this.formData.photo_pack_url) {
                    // Более простая проверка: начинается с http://, https:// или @
                    const urlPattern = /^(https?:\/\/|@)[^\s]+$/;
                    
                    if (!urlPattern.test(this.formData.photo_pack_url)) {
                        this.showError('Укажите корректную ссылку (начинается с http://, https://) или Telegram username (например: @channel_name)');
                        return;
                    }
                }

                // Отладочная информация перед отправкой
                console.log('🔍 Доступные этикетки:', this.availableLabels);
                console.log('🔍 ID этикеток в форме:', this.selectedLabels);
                console.log('🔍 Соответствия:', this.selectedLabels.map(id => {
                    const label = this.availableLabels.find(l => l.id === id);
                    return { id, found: !!label, label_name: label?.label_name };
                }));

                // Подготавливаем label_ids - только числа
                const validLabelIds = this.selectedLabels
                    .map(id => Number(id))
                    .filter(id => !isNaN(id) && id > 0);
                
                console.log('🔍 Отправляемые label_ids:', validLabelIds);
                console.log('🔍 Тип первого элемента:', typeof validLabelIds[0]);

                // Форматируем данные в правильный формат для product_data
                const productDataObj = {
                    product_id: Number(this.formData.product_id),
                    product_name: this.formData.product_name,
                    is_drop: Boolean(this.formData.is_drop),
                    retail_price: Number(this.formData.retail_price),
                    drop_price: this.formData.is_drop ? Number(this.formData.drop_price) : Number(this.formData.retail_price), // ГАРАНТИЯ: при выключенном DROP отправляем retail_price
                    size_table: this.sizeTableData.map(row => ({
                        size: row.size.trim(),
                        values: { ...row.values }
                    })),
                    label_ids: validLabelIds, // Отправляем только числа
                    photo_pack_url: this.formData.photo_pack_url || null // НОВОЕ ПОЛЕ: отправляем ссылку
                };

                // Логируем данные перед отправкой
                console.log('=== ДАННЫЕ ДЛЯ ОТПРАВКИ ===');
                console.log('product_data объект:', productDataObj);
                console.log('product_data JSON строка:', JSON.stringify(productDataObj));
                console.log('DROP режим включен?', this.formData.is_drop);
                console.log('retail_price:', this.formData.retail_price);
                console.log('drop_price (отправляемое значение):', productDataObj.drop_price);
                console.log('photo_pack_url:', productDataObj.photo_pack_url);
                
                // Проверка типов данных
                console.log('🔍 Проверка типов:');
                Object.entries(productDataObj).forEach(([key, value]) => {
                    if (key === 'label_ids') {
                        console.log(`${key}:`, value, 'тип:', typeof value, 'элементы:', value.map(v => `${v} (${typeof v})`));
                    } else {
                        console.log(`${key}:`, value, 'тип:', typeof value);
                    }
                });

                console.log('selectedFile:', this.selectedFile ? `${this.selectedFile.name} (${this.selectedFile.type}, ${this.selectedFile.size} bytes)` : 'null');

                // Создаем FormData
                const formData = new FormData();
                formData.append('product_data', JSON.stringify(productDataObj));
                
                if (this.selectedFile) {
                    formData.append('photo', this.selectedFile);
                    console.log('Фото добавлено в FormData:', this.selectedFile.name);
                }

                this.isSubmitting = true;
                
                let response;
                const endpoint = '/bot_managment/product';
                
                if (this.isAdd) {
                    console.log(`🚀 Отправка POST запроса на ${endpoint}`);
                    
                    response = await api.post(endpoint, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        timeout: 60000
                    });
                    
                    console.log('✅ Товар успешно добавлен в бот:', response.data);
                    
                } else {
                    if (!this.productData.id) {
                        this.showError('ID товара не найден');
                        this.isSubmitting = false;
                        return;
                    }
                    
                    console.log(`🔄 Отправка PUT запроса на ${endpoint}/${this.productData.id}`);
                    
                    const updateDataObj = {
                        id: Number(this.productData.id),
                        ...productDataObj
                    };
                    
                    const updateFormData = new FormData();
                    updateFormData.append('product_data', JSON.stringify(updateDataObj));
                    
                    if (this.selectedFile) {
                        updateFormData.append('photo', this.selectedFile);
                    }
                    
                    response = await api.put(`${endpoint}/${this.productData.id}`, updateFormData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        timeout: 60000
                    });
                    
                    console.log('✅ Товар успешно обновлен в боте:', response.data);
                }
                
                const result = response.data;
                console.log('📦 Полный ответ от сервера:', result);

                // ИСПРАВЛЕННАЯ ЧАСТЬ: Преобразуем label_ids в объекты этикеток для отображения
                if (result.label_ids && Array.isArray(result.label_ids)) {
                    console.log('🔍 Обработка label_ids из ответа:', result.label_ids);
                    
                    // Ищем соответствия в availableLabels
                    result.labels = result.label_ids.map(labelId => {
                        // Преобразуем ID в число для сравнения
                        const numericId = Number(labelId);
                        const label = this.availableLabels.find(l => l.id === numericId);
                        
                        // Если не нашли в availableLabels, создаем минимальный объект
                        if (!label) {
                            console.warn(`Этикетка с ID ${numericId} не найдена в availableLabels`);
                            return { 
                                id: numericId, 
                                label_name: `Этикетка #${numericId}`,
                                color: '#808080' // серый цвет по умолчанию
                            };
                        }
                        
                        console.log(`✅ Найдена этикетка: ${label.label_name} (ID: ${label.id})`);
                        return label;
                    });
                } else if (result.labels && Array.isArray(result.labels)) {
                    console.log('🔍 Обработка labels из ответа:', result.labels);
                    
                    // Если API уже вернул объекты этикеток, используем их
                    result.labels = result.labels.map(label => {
                        // Проверяем, если это уже объект
                        if (typeof label === 'object' && label !== null && label.label_name) {
                            return label;
                        } else {
                            // Если это ID или неправильный формат, ищем объект
                            const numericId = Number(label);
                            const foundLabel = this.availableLabels.find(l => l.id === numericId);
                            
                            if (foundLabel) {
                                console.log(`✅ Найдена этикетка по ID: ${foundLabel.label_name}`);
                                return foundLabel;
                            } else {
                                console.warn(`Этикетка ${label} не найдена в availableLabels`);
                                return { 
                                    id: numericId || Date.now(), 
                                    label_name: `Этикетка #${numericId || 'new'}`,
                                    color: '#808080'
                                };
                            }
                        }
                    });
                } else {
                    console.warn('В ответе сервера нет данных об этикетках');
                    result.labels = [];
                }
                
                console.log('🎯 Итоговые этикетки для отображения:', result.labels);

                this.$emit('submit', result, this.isAdd ? 'added' : 'updated');
                
                const message = this.isAdd ? 'Товар успешно добавлен в бот!' : 'Товар успешно обновлен!';
                this.showSuccess(message);
                
                if (this.isAdd) {
                    this.resetForm();
                }
                
                return result;
                
            } catch (error) {
                console.error('❌ Ошибка при отправке формы:', error);
                
                if (error.response) {
                    console.error('❌ Статус ошибки:', error.response.status);
                    console.error('❌ Данные ошибки:', error.response.data);
                    
                    if (error.response.data && error.response.data.detail) {
                        console.error('❌ Детали ошибки:');
                        if (Array.isArray(error.response.data.detail)) {
                            error.response.data.detail.forEach((err, idx) => {
                                console.error(`  ${idx + 1}. loc: ${err.loc}, msg: ${err.msg}, type: ${err.type}`);
                            });
                        } else {
                            console.error('  detail:', error.response.data.detail);
                        }
                    }
                } else if (error.request) {
                    console.error('❌ Запрос был отправлен, но ответ не получен:', error.request);
                } else {
                    console.error('❌ Ошибка настройки запроса:', error.message);
                }
                
                let errorMessage = 'Произошла ошибка при сохранении товара';
                
                if (error.response?.data) {
                    const data = error.response.data;
                    
                    if (typeof data === 'string') {
                        errorMessage = data;
                    } else if (data.detail) {
                        if (Array.isArray(data.detail)) {
                            const errors = data.detail.map(err => {
                                const field = err.loc && err.loc.length > 1 ? err.loc[1] : 'данные';
                                return `${field}: ${err.msg}`;
                            });
                            errorMessage = `Ошибки валидации: ${errors.join(', ')}`;
                        } else if (typeof data.detail === 'string') {
                            errorMessage = data.detail;
                        }
                    }
                }
                
                if (errorMessage.includes('label_ids') || errorMessage.includes('int_type')) {
                    errorMessage = 'Ошибка в данных этикеток: должны быть числовые ID';
                }
                
                this.showError(errorMessage);
                
            } finally {
                this.isSubmitting = false;
            }
        },
        
        resetForm() {
            this.formData = {
                product_id: null,
                product_name: '',
                is_drop: false,
                drop_price: 0,
                retail_price: 0,
                photo_url: '',
                photo_pack_url: '', // НОВОЕ ПОЛЕ: сбрасываем ссылку
                size_table: [],
                label_ids: [] // Пустой массив чисел
            };
            
            this.sizeTableData = [];
            this.characteristics = ['Длина', 'Талия', 'Выход (застегнуто)', 'Выход (растегнуто)'];
            
            this.photo = null;
            this.selectedFile = null;
            this.isFormFilled = false;
            
            this.addNewSizeRow();
        },
        
        showSuccess(message) {
            alert(`✅ ${message}`);
        },
        
        showError(message) {
            alert(`❌ ${message}`);
        }
    }
};
</script>

<style scoped>
/* Основные стили */
.tg-product-modal-fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #181818 0%, #1a1a2e 100%);
    color: #ffffff;
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: slideIn 0.3s ease;
}

@keyframes slideIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

/* Шапка формы */
.modify-form-header {
    text-align: center;
    margin-bottom: 30px;
    padding-bottom: 15px;
    border-bottom: 2px solid rgba(37, 165, 240, 0.3);
    flex-shrink: 0;
    padding: 15px 20px;
    background: rgba(0, 0, 0, 0.3);
}

.header-icon {
    font-size: 40px;
    margin-bottom: 12px;
    animation: float 3s ease-in-out infinite;
}

@keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
}

.modify-form-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 6px 0;
    background: linear-gradient(135deg, #25A5F0 0%, #4ECDC4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.header-subtitle {
    color: #a0a0a0;
    font-size: 14px;
    margin: 0;
}

/* Контейнер для прокрутки */
.form-scroll-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
}

.tg-product-form-fullscreen {
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
}

/* Секции формы */
.form-section-fullscreen {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 20px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.form-section-fullscreen:hover {
    border-color: rgba(37, 165, 240, 0.2);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.section-title-fullscreen {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #25A5F0;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-icon-fullscreen {
    font-size: 20px;
    background: rgba(37, 165, 240, 0.1);
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Стили для секции с паком фотографий (НОВЫЕ СТИЛИ) */
.photo-pack-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.url-input-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.url-input-wrapper {
    position: relative;
    width: 100%;
}

.url-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    color: #25A5F0;
    opacity: 0.7;
    z-index: 1;
}

.url-input {
    width: 100%;
    padding: 16px 16px 16px 48px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    color: #ffffff;
    font-size: 15px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.url-input:focus {
    outline: none;
    border-color: #25A5F0;
    box-shadow: 0 0 0 3px rgba(37, 165, 240, 0.2);
    background: rgba(255, 255, 255, 0.12);
}

.url-input:hover {
    border-color: rgba(37, 165, 240, 0.3);
}

.url-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

.url-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: rgba(37, 165, 240, 0.1);
    border: 1px solid rgba(37, 165, 240, 0.2);
    border-radius: 8px;
    animation: slideInInfo 0.3s ease;
}

.current-photo-pack {
    margin-top: 8px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    animation: slideInInfo 0.3s ease;
}

.pack-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.pack-icon {
    font-size: 16px;
    color: #4ECDC4;
}

.pack-label {
    font-size: 13px;
    color: #a0a0a0;
}

.pack-link {
    flex: 1;
    font-size: 13px;
    color: #25A5F0;
    text-decoration: none;
    word-break: break-all;
    transition: all 0.3s ease;
}

.pack-link:hover {
    text-decoration: underline;
    color: #4ECDC4;
}

.remove-pack-btn {
    background: rgba(255, 59, 48, 0.1);
    border: none;
    color: #FF3B30;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.remove-pack-btn:hover {
    background: rgba(255, 59, 48, 0.2);
    transform: scale(1.1);
}

/* Сетка формы */
.form-grid-fullscreen {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
}

/* Группы форм */
.form-group-fullscreen {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label-fullscreen {
    font-size: 13px;
    font-weight: 600;
    color: #e0e0e0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.label-icon-fullscreen {
    font-size: 14px;
    opacity: 0.8;
}

.form-hint-fullscreen {
    font-size: 12px;
    color: #a0a0a0;
    margin: 4px 0 0 0;
    font-style: italic;
    line-height: 1.4;
}

/* Кастомный селект товара */
.custom-select-wrapper {
    position: relative;
    width: 100%;
}

.custom-select {
    width: 100%;
    padding: 16px 48px 16px 16px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.3s ease;
    appearance: none;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.custom-select:focus {
    outline: none;
    border-color: #25A5F0;
    box-shadow: 0 0 0 3px rgba(37, 165, 240, 0.2);
    background: rgba(255, 255, 255, 0.12);
}

.custom-select:hover {
    border-color: rgba(37, 165, 240, 0.3);
}

.select-arrow {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    pointer-events: none;
    transition: all 0.3s ease;
}

.custom-select:focus + .select-arrow {
    color: #25A5F0;
    transform: translateY(-50%) rotate(180deg);
}

.placeholder-option {
    color: rgba(255, 255, 255, 0.4);
}

.select-option {
    color: #ffffff;
    background: rgba(0, 0, 0, 0.8);
    padding: 12px;
}

.select-option:disabled {
    color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.05);
}

.option-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.option-badge {
    background: rgba(255, 59, 48, 0.2);
    color: #FF3B30;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
    float: right;
}

.selected-product-info {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 10px 12px;
    background: rgba(76, 217, 100, 0.1);
    border: 1px solid rgba(76, 217, 100, 0.2);
    border-radius: 8px;
    animation: slideInInfo 0.3s ease;
}

@keyframes slideInInfo {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
}

.info-icon {
    color: #4CD964;
    font-weight: bold;
    font-size: 14px;
}

.info-text {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
}

/* Отображение названия товара */
.product-name-display-fullscreen {
    padding: 16px;
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.1) 0%, rgba(37, 165, 240, 0.05) 100%);
    border: 2px solid rgba(37, 165, 240, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s ease;
}

.product-name-display-fullscreen:hover {
    border-color: rgba(37, 165, 240, 0.3);
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.15) 0%, rgba(37, 165, 240, 0.08) 100%);
}

.name-icon {
    font-size: 20px;
    color: #25A5F0;
    opacity: 0.8;
}

.product-name-text {
    font-weight: 600;
    font-size: 16px;
    color: #25A5F0;
    flex: 1;
}

/* DROP режим - улучшенный */
.drop-mode-toggle {
    margin-top: 8px;
}

.drop-mode-btn {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 20px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    color: #a0a0a0;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: left;
    position: relative;
    overflow: hidden;
}

.drop-mode-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.drop-mode-btn:hover::before {
    opacity: 1;
}

.drop-mode-btn:hover {
    border-color: rgba(37, 165, 240, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.drop-mode-btn.active {
    background: linear-gradient(135deg, rgba(255, 107, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%);
    border-color: #FF6B00;
    color: #FFA500;
    box-shadow: 0 6px 20px rgba(255, 107, 0, 0.2);
}

.drop-mode-btn.active:hover {
    border-color: #FF8C00;
    box-shadow: 0 8px 24px rgba(255, 107, 0, 0.3);
    transform: translateY(-3px);
}

.drop-mode-content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
}

.drop-mode-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    flex-shrink: 0;
}

.drop-mode-btn.active .drop-mode-icon-wrapper {
    background: linear-gradient(135deg, #FF6B00 0%, #FFA500 100%);
}

.drop-mode-icon {
    font-size: 24px;
    color: inherit;
}

.drop-mode-text-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.drop-mode-text {
    font-size: 16px;
    font-weight: 700;
    color: inherit;
}

.drop-mode-subtext {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.6);
}

.drop-mode-badge {
    background: linear-gradient(135deg, #FF6B00 0%, #FFA500 100%);
    color: white;
    padding: 8px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    animation: badgePulse 2s infinite;
    box-shadow: 0 4px 12px rgba(255, 107, 0, 0.3);
}

@keyframes badgePulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
}

.badge-icon {
    font-size: 14px;
}

.badge-text {
    white-space: nowrap;
}

/* Контейнер для ценовых полей */
.price-fields-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 8px;
}

.price-field {
    position: relative;
}

.price-input-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.price-input-wrapper {
    position: relative;
    width: 100%;
}

.price-input {
    width: 100%;
    padding: 18px 60px 18px 16px;
    background: rgba(255, 255, 255, 0.08);
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.price-input:focus {
    outline: none;
    border-color: #25A5F0;
    box-shadow: 0 0 0 3px rgba(37, 165, 240, 0.2);
    background: rgba(255, 255, 255, 0.12);
}

.price-input:hover {
    border-color: rgba(37, 165, 240, 0.3);
}

.price-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
    font-weight: normal;
}

.currency-display {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #25A5F0;
    font-weight: 700;
    font-size: 18px;
    pointer-events: none;
}

/* Специальные стили для DROP цены */
.drop-price .price-input {
    background: linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
    border-color: rgba(255, 107, 0, 0.3);
}

.drop-price .price-input:focus {
    border-color: #FF6B00;
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.2);
}

.drop-price .currency-display {
    color: #FF6B00;
}

/* Информация о цене */
.price-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    animation: slideInInfo 0.3s ease;
}

.discount-text {
    color: #4CD964;
    font-weight: 700;
}

/* Таблица размеров */
.size-table-json-section {
    margin-top: 10px;
}

.size-table-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 15px 0;
}

.control-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.control-icon {
    font-size: 16px;
}

.add-btn {
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.15) 0%, rgba(37, 165, 240, 0.1) 100%);
    border: 1px solid rgba(37, 165, 240, 0.3);
    color: #25A5F0;
}

.add-btn:hover {
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.2) 0%, rgba(37, 165, 240, 0.15) 100%);
    border-color: #25A5F0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 165, 240, 0.2);
}

.add-char-btn {
    background: linear-gradient(135deg, rgba(76, 217, 100, 0.15) 0%, rgba(76, 217, 100, 0.1) 100%);
    border: 1px solid rgba(76, 217, 100, 0.3);
    color: #4CD964;
}

.add-char-btn:hover {
    background: linear-gradient(135deg, rgba(76, 217, 100, 0.2) 0%, rgba(76, 217, 100, 0.15) 100%);
    border-color: #4CD964;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(76, 217, 100, 0.2);
}

.remove-btn {
    background: linear-gradient(135deg, rgba(255, 59, 48, 0.15) 0%, rgba(255, 59, 48, 0.1) 100%);
    border: 1px solid rgba(255, 59, 48, 0.3);
    color: #FF3B30;
}

.remove-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(255, 59, 48, 0.2) 0%, rgba(255, 59, 48, 0.15) 100%);
    border-color: #FF3B30;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 59, 48, 0.2);
}

.remove-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
}

/* Контейнер таблицы */
.table-container {
    width: 100%;
    overflow-x: auto;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    margin-bottom: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* АДАПТИВНАЯ таблица */
.simple-size-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    min-width: 100%;
}

/* Заголовки таблицы */
.simple-size-table th {
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.15) 0%, rgba(37, 165, 240, 0.1) 100%);
    color: #25A5F0;
    font-weight: 600;
    font-size: 13px;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 2px solid rgba(37, 165, 240, 0.3);
    padding: 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
    vertical-align: middle;
    white-space: nowrap;
}

/* Ячейки таблицы */
.simple-size-table td {
    background: rgba(255, 255, 255, 0.03);
    font-size: 14px;
    transition: background-color 0.3s ease;
    padding: 14px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
    vertical-align: middle;
}

.simple-size-table tr:hover td {
    background: rgba(255, 255, 255, 0.06);
}

/* Специфичные стили для колонок */
.size-header {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.size-cell {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.char-header {
    min-width: 100px;
    max-width: 200px;
}

.char-cell {
    min-width: 100px;
    max-width: 200px;
}

.actions-header {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
}

.actions-cell {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
}

/* Содержимое заголовков */
.header-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
}

.char-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    padding: 0 4px;
}

/* Кнопка удаления характеристики */
.remove-char-btn {
    background: none;
    border: none;
    color: #ff3b30;
    font-size: 11px;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    transition: all 0.3s ease;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.remove-char-btn:hover {
    background: rgba(255, 59, 48, 0.1);
}

/* Поля ввода внутри таблицы */
.size-input,
.char-input {
    width: 100%;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    transition: all 0.3s ease;
    text-align: center;
    box-sizing: border-box;
    min-width: 0;
}

.size-input:focus,
.char-input:focus {
    outline: none;
    border-color: #25A5F0;
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 2px rgba(37, 165, 240, 0.2);
}

.size-input {
    font-weight: 600;
}

.char-input {
    font-family: monospace;
}

/* Кнопка удаления строки */
.remove-row-btn {
    background: rgba(255, 59, 48, 0.1);
    border: 1px solid rgba(255, 59, 48, 0.3);
    color: #FF3B30;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
}

.remove-row-btn:hover {
    background: rgba(255, 59, 48, 0.2);
    border-color: #FF3B30;
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(255, 59, 48, 0.2);
}

/* Предпросмотр JSON */
.json-preview {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 16px;
    margin-top: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.preview-title {
    font-size: 15px;
    font-weight: 600;
    color: #4ECDC4;
    margin: 0 0 12px 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.preview-title::before {
    content: "{}";
    font-size: 14px;
}

.json-output {
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 14px;
    font-size: 12px;
    color: #4ECDC4;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    line-height: 1.6;
}

/* Сообщение если нет размеров */
.no-sizes-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.05) 0%, rgba(37, 165, 240, 0.02) 100%);
    border-radius: 12px;
    border: 2px dashed rgba(37, 165, 240, 0.2);
    text-align: center;
    gap: 15px;
    transition: all 0.3s ease;
}

.no-sizes-message:hover {
    border-color: rgba(37, 165, 240, 0.3);
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.08) 0%, rgba(37, 165, 240, 0.04) 100%);
}

.message-icon {
    font-size: 48px;
    opacity: 0.7;
    color: #25A5F0;
}

.message-text {
    color: #a0a0a0;
    font-size: 14px;
    margin: 0;
    max-width: 300px;
    line-height: 1.5;
}

/* Фото товара */
.photo-upload-section-fullscreen {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.current-photo-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.photo-preview-fullscreen {
    position: relative;
    width: 160px;
    height: 160px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
}

.photo-preview-fullscreen:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
}

.preview-img-fullscreen {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.remove-photo-btn-fullscreen {
    position: absolute;
    top: -8px;
    right: -8px;
    background: linear-gradient(135deg, #FF3B30 0%, #FF6B00 100%);
    border: none;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(255, 59, 48, 0.4);
    z-index: 1000;
}

.remove-photo-btn-fullscreen:hover {
    transform: scale(1.15);
    box-shadow: 0 6px 18px rgba(255, 59, 48, 0.6);
}

.current-photo-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgba(37, 165, 240, 0.1);
    border: 1px solid rgba(37, 165, 240, 0.2);
    border-radius: 10px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
}

.file-upload-fullscreen {
    width: 100%;
}

.file-input-fullscreen {
    display: none;
}

.file-upload-label-fullscreen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 40px 20px;
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.1) 0%, rgba(37, 165, 240, 0.05) 100%);
    border: 2px dashed rgba(37, 165, 240, 0.4);
    border-radius: 20px;
    color: #25A5F0;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: center;
    min-height: 200px;
    position: relative;
    overflow: hidden;
}

.file-upload-label-fullscreen::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.15) 0%, rgba(37, 165, 240, 0.08) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.file-upload-label-fullscreen:hover::before {
    opacity: 1;
}

.file-upload-label-fullscreen:hover {
    border-color: #25A5F0;
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(37, 165, 240, 0.25);
}

.file-upload-label-fullscreen.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
}

.size-table-recognition-section {
    margin-bottom: 24px;
}

.size-table-recognition-section .file-upload-label-fullscreen {
    min-height: 100px;
    padding: 20px;
    flex-direction: row;
}

.upload-icon-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    z-index: 1;
}

.upload-icon-circle {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #25A5F0 0%, #4ECDC4 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(37, 165, 240, 0.3);
}

.upload-icon-fullscreen {
    font-size: 28px;
    color: white;
}

.upload-text-fullscreen {
    font-size: 18px;
    font-weight: 700;
}

.upload-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    z-index: 1;
}

.upload-hint-fullscreen {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: normal;
    font-style: italic;
}

/* Этикетки */
.labels-section-fullscreen {
    margin-top: 10px;
}

.available-labels-fullscreen {
    margin-bottom: 20px;
}

.labels-grid-fullscreen,
.selected-labels-list-fullscreen {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
}

.label-item-fullscreen,
.selected-label-item-fullscreen {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    font-size: 13px;
    position: relative;
    overflow: hidden;
}

.label-item-fullscreen::before,
.selected-label-item-fullscreen::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.label-item-fullscreen:hover::before,
.selected-label-item-fullscreen:hover::before {
    opacity: 1;
}

.label-item-fullscreen:hover {
    border-color: rgba(37, 165, 240, 0.3);
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.label-item-fullscreen.selected {
    background: rgba(37, 165, 240, 0.2);
    border-color: #25A5F0;
    box-shadow: 0 4px 12px rgba(37, 165, 240, 0.3);
}

.label-content {
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1;
}

.label-check-icon {
    color: #25A5F0;
    font-weight: bold;
    font-size: 14px;
    margin-left: 4px;
}

.selected-label-item-fullscreen {
    background: rgba(37, 165, 240, 0.15);
    border-color: rgba(37, 165, 240, 0.5);
}

.selected-label-content {
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1;
}

.label-color-dot-fullscreen {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.label-name-fullscreen {
    white-space: nowrap;
    flex: 1;
    font-weight: 600;
}

.remove-label-btn-fullscreen {
    background: none;
    border: none;
    color: #ff3b30;
    font-size: 13px;
    cursor: pointer;
    padding: 4px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.3s ease;
    opacity: 0.7;
    z-index: 1;
}

.remove-label-btn-fullscreen:hover {
    opacity: 1;
    background: rgba(255, 59, 48, 0.1);
    transform: scale(1.1);
}

/* Сообщение если нет этикеток */
.no-labels-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 20px;
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.05) 0%, rgba(37, 165, 240, 0.02) 100%);
    border-radius: 12px;
    border: 2px dashed rgba(37, 165, 240, 0.2);
    text-align: center;
    gap: 12px;
    transition: all 0.3s ease;
}

.no-labels-message:hover {
    border-color: rgba(37, 165, 240, 0.3);
    background: linear-gradient(135deg, rgba(37, 165, 240, 0.08) 0%, rgba(37, 165, 240, 0.04) 100%);
}

.no-labels-message .message-icon {
    font-size: 36px;
    opacity: 0.7;
    color: #25A5F0;
}

.no-labels-message .message-text {
    color: #a0a0a0;
    font-size: 13px;
    margin: 0;
    max-width: 280px;
    line-height: 1.5;
}

/* Кнопки действий */
.form-actions-fullscreen {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    padding: 24px;
    margin: 20px -20px -20px;
}

.btn-fullscreen {
    padding: 18px 48px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    position: relative;
    overflow: hidden;
}

.btn-fullscreen::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.btn-fullscreen:hover::before {
    opacity: 1;
}

.btn-fullscreen:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

.btn-fullscreen:disabled:hover::before {
    opacity: 0;
}

.btn-submit-fullscreen {
    background: linear-gradient(135deg, #25A5F0 0%, #4ECDC4 100%);
    color: white;
    box-shadow: 0 6px 20px rgba(37, 165, 240, 0.4);
}

.btn-submit-fullscreen:hover:not(:disabled) {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(37, 165, 240, 0.6);
}

.btn-content {
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 1;
}

.btn-icon {
    font-size: 20px;
}

.btn-text {
    white-space: nowrap;
}

.submit-spinner-fullscreen {
    width: 24px;
    height: 24px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    z-index: 1;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Адаптивность */
@media (max-width: 768px) {
    .modify-form-header {
        padding: 12px 16px;
    }
    
    .header-icon {
        font-size: 28px;
    }
    
    .modify-form-header h1 {
        font-size: 18px;
    }
    
    .header-subtitle {
        font-size: 12px;
    }
    
    .form-scroll-container {
        padding: 16px;
    }
    
    .form-section-fullscreen {
        padding: 20px;
        border-radius: 14px;
    }
    
    .form-grid-fullscreen {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .size-table-controls {
        flex-direction: column;
    }
    
    .control-btn {
        width: 100%;
        justify-content: center;
        padding: 14px 16px;
    }
    
    /* Адаптивность таблицы */
    .size-header,
    .size-cell {
        width: 100px;
        min-width: 100px;
        max-width: 100px;
    }
    
    .char-header,
    .char-cell {
        min-width: 90px;
        max-width: 150px;
    }
    
    .actions-header,
    .actions-cell {
        width: 90px;
        min-width: 90px;
        max-width: 90px;
    }
    
    .size-input,
    .char-input {
        font-size: 12px;
        padding: 10px 12px;
    }
    
    .remove-char-btn {
        font-size: 10px;
        width: 20px;
        height: 20px;
    }
    
    .remove-row-btn {
        width: 32px;
        height: 32px;
        font-size: 14px;
    }
    
    .simple-size-table th,
    .simple-size-table td {
        padding: 12px 10px;
        font-size: 12px;
    }
    
    .drop-mode-content {
        gap: 12px;
    }
    
    .drop-mode-icon-wrapper {
        width: 40px;
        height: 40px;
    }
    
    .drop-mode-text {
        font-size: 14px;
    }
    
    .drop-mode-subtext {
        font-size: 11px;
    }
    
    .price-input {
        padding: 16px 56px 16px 14px;
        font-size: 16px;
    }
    
    .currency-display {
        font-size: 16px;
    }
    
    .custom-select {
        padding: 14px 44px 14px 14px;
        font-size: 14px;
    }
    
    .photo-preview-fullscreen {
        width: 140px;
        height: 140px;
    }
    
    .file-upload-label-fullscreen {
        padding: 32px 16px;
        min-height: 180px;
    }
    
    .upload-icon-circle {
        width: 56px;
        height: 56px;
    }
    
    .upload-icon-fullscreen {
        font-size: 24px;
    }
    
    .upload-text-fullscreen {
        font-size: 16px;
    }
    
    .upload-hint-fullscreen {
        font-size: 13px;
    }
    
    /* Адаптивность для секции с паком фотографий */
    .url-input {
        padding: 14px 14px 14px 42px;
        font-size: 14px;
    }
    
    .url-icon {
        left: 14px;
        font-size: 14px;
    }
    
    .pack-info {
        gap: 8px;
    }
    
    .pack-link {
        font-size: 12px;
    }
    
    .remove-pack-btn {
        width: 24px;
        height: 24px;
        font-size: 12px;
    }
    
    .form-actions-fullscreen {
        flex-direction: column;
        align-items: center;
        padding: 20px 16px;
    }
    
    .btn-fullscreen {
        min-width: 100%;
        padding: 16px 32px;
        font-size: 16px;
    }
}

@media (max-width: 480px) {
    .modify-form-header h1 {
        font-size: 16px;
    }
    
    .header-subtitle {
        font-size: 11px;
    }
    
    .form-scroll-container {
        padding: 12px;
    }
    
    .section-title-fullscreen {
        font-size: 16px;
    }
    
    .section-icon-fullscreen {
        width: 32px;
        height: 32px;
        font-size: 18px;
    }
    
    /* Адаптивность таблицы для телефонов */
    .size-header,
    .size-cell {
        width: 80px;
        min-width: 80px;
        max-width: 80px;
    }
    
    .char-header,
    .char-cell {
        min-width: 70px;
        max-width: 120px;
    }
    
    .actions-header,
    .actions-cell {
        width: 70px;
        min-width: 70px;
        max-width: 70px;
    }
    
    .size-input,
    .char-input {
        font-size: 11px;
        padding: 8px 10px;
    }
    
    .remove-char-btn {
        font-size: 9px;
        width: 18px;
        height: 18px;
        padding: 3px 4px;
    }
    
    .remove-row-btn {
        width: 28px;
        height: 28px;
        font-size: 12px;
    }
    
    .simple-size-table th,
    .simple-size-table td {
        padding: 10px 8px;
        font-size: 11px;
    }
    
    .drop-mode-btn {
        padding: 16px;
    }
    
    .drop-mode-content {
        gap: 10px;
    }
    
    .drop-mode-icon-wrapper {
        width: 36px;
        height: 36px;
    }
    
    .drop-mode-icon {
        font-size: 20px;
    }
    
    .drop-mode-text {
        font-size: 13px;
    }
    
    .drop-mode-subtext {
        font-size: 10px;
    }
    
    .price-input {
        padding: 14px 52px 14px 12px;
        font-size: 15px;
    }
    
    .currency-display {
        font-size: 15px;
        right: 14px;
    }
    
    .custom-select {
        padding: 12px 40px 12px 12px;
        font-size: 13px;
    }
    
    .photo-preview-fullscreen {
        width: 120px;
        height: 120px;
    }
    
    .remove-photo-btn-fullscreen {
        width: 28px;
        height: 28px;
        font-size: 14px;
        top: -6px;
        right: -6px;
    }
    
    .file-upload-label-fullscreen {
        padding: 28px 12px;
        min-height: 160px;
    }
    
    .upload-icon-circle {
        width: 48px;
        height: 48px;
    }
    
    .upload-icon-fullscreen {
        font-size: 20px;
    }
    
    .upload-text-fullscreen {
        font-size: 15px;
    }
    
    .upload-hint-fullscreen {
        font-size: 12px;
    }
    
    /* Адаптивность для секции с паком фотографий */
    .url-input {
        padding: 12px 12px 12px 38px;
        font-size: 13px;
    }
    
    .url-icon {
        left: 12px;
        font-size: 13px;
    }
    
    .url-info {
        font-size: 12px;
        padding: 8px 10px;
    }
    
    .current-photo-pack {
        padding: 10px 12px;
    }
    
    .pack-info {
        font-size: 12px;
    }
    
    .pack-link {
        font-size: 11px;
    }
    
    .remove-pack-btn {
        width: 22px;
        height: 22px;
        font-size: 11px;
    }
}

/* Скроллбары */
.tg-product-modal-fullscreen::-webkit-scrollbar {
    width: 6px;
}

.tg-product-modal-fullscreen::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
}

.tg-product-modal-fullscreen::-webkit-scrollbar-thumb {
    background: #25A5F0;
    border-radius: 4px;
}

.tg-product-modal-fullscreen::-webkit-scrollbar-thumb:hover {
    background: #1e8bc4;
}

.form-scroll-container::-webkit-scrollbar {
    width: 6px;
}

.form-scroll-container::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
}

.form-scroll-container::-webkit-scrollbar-thumb {
    background: #25A5F0;
    border-radius: 4px;
}

.form-scroll-container::-webkit-scrollbar-thumb:hover {
    background: #1e8bc4;
}

.table-container::-webkit-scrollbar {
    height: 8px;
}

.table-container::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
    background: rgba(37, 165, 240, 0.6);
    border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
    background: rgba(37, 165, 240, 0.8);
}

.json-output::-webkit-scrollbar {
    width: 6px;
}

.json-output::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
}

.json-output::-webkit-scrollbar-thumb {
    background: #4ECDC4;
    border-radius: 3px;
}

.json-output::-webkit-scrollbar-thumb:hover {
    background: #3bb8b0;
}
</style>
